import { z } from 'zod';
import { MotorSchema } from '@remoto/protocolo';
import { PapelSchema } from '../comum/papeis';

/**
 * Contrato da exportação de conversas para o cérebro (F14).
 *
 * O cérebro é um serviço FORA do painel — índice semântico das conversas da
 * frota, servido por MCP. Ele lê daqui e nunca do banco: a direção da conexão
 * é a mesma do daemon (de fora para dentro, só saída), e é isso que o deixa
 * mudar de casa — do PC de hoje para uma VPS dedicada depois — sem que ninguém
 * precise de credencial de Postgres na outra rede.
 *
 * Duas fontes, UMA forma: a página desta rota e o JSONL do experimento
 * (exportado por SQL para o benchmark) carregam exatamente `CerebroEvento`.
 * Se divergirem, o pipeline que foi medido não é o que roda.
 */

/** O prefixo do token de serviço, como `rmt_` é o do token de dispositivo. */
export const PREFIXO_TOKEN_SERVICO = 'rms_';

/**
 * Um turno de conversa, já com o texto extraído.
 *
 * `texto` sai SEMPRE de `remoto_texto_do_evento(carga)` — a mesma função SQL
 * imutável que o índice GIN da busca usa, e que tem espelho em TS
 * (`textoDoEvento`) e fixtures compartilhadas. Não existe terceira cópia da
 * regra: o cérebro recebe texto pronto, nunca `carga` crua. Blocos `thinking`
 * não passam por ali (ADR-0006), e é assim que eles ficam fora do índice.
 */
export const CerebroEventoSchema = z.strictObject({
  sessaoId: z.string().min(1),
  seq: z.number().int(),
  /**
   * A chave de dedupe. Só evento COM uuid é exportado: sem ele não há
   * idempotência nenhuma (no Postgres `NULL` nunca é igual a `NULL`, e o
   * índice único não protege linha sem uuid — medido: 25 linhas `mode` num
   * transcript viraram 48 no banco). Os tipos sem uuid são metadado de
   * controle, que não se indexa mesmo.
   *
   * Chamava-se `uuidClaude` até este passo, e o schema é `strictObject` por
   * causa disso: com `z.object`, um produtor que ainda mandasse o nome velho
   * ao lado do novo teria a chave velha DESCARTADA em silêncio, e ninguém
   * saberia qual dos dois lados estava atrasado. Recusa dura em vez de
   * degradação — a mesma escolha do `SdkEstado` no fio.
   */
  uuidTurno: z.string().min(1),
  tipo: z.enum(['user', 'assistant']),
  texto: z.string(),
  criadoEm: z.string(),
  /**
   * Qual CLI produziu este turno.
   *
   * Viaja por EVENTO, e não só por sessão, porque quem o consome é o pedaço
   * no índice: a busca filtra por motor (`buscar_dialogos`), e o pedaço não
   * tem como voltar à sessão para descobrir de onde veio. Ele também sai no
   * resumo por sessão, que é outra pergunta ("o que existe"), não a mesma
   * repetida.
   */
  motor: MotorSchema,
  /** Escopo: o cérebro filtra por máquina visível e por função da sessão. */
  maquinaId: z.string().min(1),
  maquinaNome: z.string(),
  maquinaVisibilidade: PapelSchema,
  funcao: z.enum(['NORMAL', 'MESTRE']),
  cwd: z.string(),
  sessaoTitulo: z.string(),
});
export type CerebroEvento = z.infer<typeof CerebroEventoSchema>;

/**
 * Cursor keyset. Não é OFFSET: a tabela é append-only e cresce durante a
 * paginação, e `OFFSET` pularia linha calado quando isso acontece.
 *
 * A ordem é `(criadoEm, sessaoId, seq)` — `criadoEm` sozinho não é único.
 */
export const CerebroCursorSchema = z.object({
  criadoEm: z.string(),
  sessaoId: z.string(),
  seq: z.number().int(),
});
export type CerebroCursor = z.infer<typeof CerebroCursorSchema>;

export const CerebroEventosQuerySchema = z.object({
  cursorCriadoEm: z.string().optional(),
  cursorSessaoId: z.string().optional(),
  cursorSeq: z.coerce.number().int().optional(),
  limite: z.coerce.number().int().min(1).max(1000).default(500),
  /**
   * Turno curto demais não é conversa: é "ok", "sim", "continue". Entra como
   * padrão e não como constante escondida porque é o MESMO corte do corpus do
   * benchmark — mudar aqui invalida a comparação com a linha de base.
   */
  minimoChars: z.coerce.number().int().min(0).max(10_000).default(80),
});
export type CerebroEventosQuery = z.infer<typeof CerebroEventosQuerySchema>;

/**
 * O retrato das sessoes aceita o MESMO `minimoChars` da exportacao: a
 * reconciliacao compara a contagem daqui com a do indice, e duas regras de
 * corte diferentes fariam toda sessao com turno curto parecer atrasada.
 */
export const CerebroSessoesQuerySchema = z.object({
  minimoChars: z.coerce.number().int().min(0).max(10_000).default(80),
});
export type CerebroSessoesQuery = z.infer<typeof CerebroSessoesQuerySchema>;

export const CerebroLoteSchema = z.object({
  eventos: z.array(CerebroEventoSchema),
  /** `null` quando acabou. Cliente que ignora isso pagina para sempre. */
  proximoCursor: CerebroCursorSchema.nullable(),
});
export type CerebroLote = z.infer<typeof CerebroLoteSchema>;

/**
 * Resumo por sessão, para a reconciliação.
 *
 * Existe por causa da poda: `RETENCAO_EVENTOS_DIAS` apaga os eventos de
 * sessões encerradas há mais de 180 dias, e o índice não tem como saber disso
 * sozinho — ele só veria a sessão parar de crescer. Sessão com zero eventos
 * aqui e pedaços lá foi podada.
 */
export const CerebroResumoDeSessaoSchema = z.object({
  id: z.string().min(1),
  maquinaId: z.string().min(1),
  maquinaVisibilidade: PapelSchema,
  funcao: z.enum(['NORMAL', 'MESTRE']),
  /** O motor desta sessão; o de cada turno vai no próprio `CerebroEvento`. */
  motor: MotorSchema,
  titulo: z.string(),
  cwd: z.string(),
  encerradaEm: z.string().nullable(),
  arquivadaEm: z.string().nullable(),
  eventosComTexto: z.number().int(),
  maxSeq: z.number().int(),
});
export type CerebroResumoDeSessao = z.infer<typeof CerebroResumoDeSessaoSchema>;

export const CerebroMaquinaSchema = z.object({
  id: z.string().min(1),
  nome: z.string(),
  visibilidade: PapelSchema,
});
export type CerebroMaquina = z.infer<typeof CerebroMaquinaSchema>;

export const CerebroSessoesRespostaSchema = z.object({
  sessoes: z.array(CerebroResumoDeSessaoSchema),
  /** Substitui a tabela local inteira a cada ciclo: visibilidade muda. */
  maquinas: z.array(CerebroMaquinaSchema),
});
export type CerebroSessoesResposta = z.infer<typeof CerebroSessoesRespostaSchema>;

/*
  Não há `CerebroPulsoSchema` aqui, e a ausência é a decisão (E13).

  A forma existia para uma rota que ninguém chamava e um campo que ninguém
  lia. O que o cérebro relata de si mesmo chega pelo DAEMON, que lê `/status`
  do bridge e o põe no `agente.pulso` — `AgenteCerebroSchema`, em
  `packages/protocolo`. Duas formas para o mesmo fato divergem no dia em que
  uma delas ganhar um campo.
*/
